import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { isOnline as netIsOnline, isOffline as netIsOffline } from '../utils/netStatus';

const days = ['Bazar', 'Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə', 'Şənbə'];
const months = ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'];

// Saat ayrıca komponentdir — hər saniyə yalnız bu re-render olur, bütün səhifə yox
// (əks halda fon şəkli və logolar hər saniyə yenidən yüklənir = flicker).
function LoginClock() {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);
    const timeStr = [time.getHours(), time.getMinutes(), time.getSeconds()]
        .map((v) => String(v).padStart(2, '0')).join(':');
    const dateStr = `${days[time.getDay()]}, ${time.getDate()} ${months[time.getMonth()]} ${time.getFullYear()}`;
    return (
        <>
            <div className="mb-2 font-mono text-2xl text-white sm:text-3xl md:text-4xl">{timeStr}</div>
            <div className="text-xs text-white/85 sm:text-sm">{dateStr}</div>
        </>
    );
}

const defaultScreenConfig = {
    tenant_name: null,
    login_image_url: null,
    app_name: 'Fiscal POS',
    app_version: '1.0.0',
    powered_by_url: 'https://www.fiscalbusiness.net'
};

const getCachedScreenConfig = (token) => {
    if (!token) return defaultScreenConfig;

    try {
        const raw = localStorage.getItem(`pos_login_screen_${token}`);
        if (!raw) return defaultScreenConfig;

        return {
            ...defaultScreenConfig,
            ...JSON.parse(raw)
        };
    } catch {
        return defaultScreenConfig;
    }
};

const getSavedAdminUsername = () => {
    try {
        return localStorage.getItem('pos_last_admin_username') || '';
    } catch {
        return '';
    }
};

// Offline: giriş ekranındakı istifadəçi siyahısı (online-da son alınan, yoxdursa offline giriş paketindən)
const ROLE_LABEL = { admin: 'admin', kassir: 'kassir', administrator: 'administrator', anbardar: 'anbardar' };
const offlineTenantLogins = (token) => {
    try {
        const saved = JSON.parse(localStorage.getItem(`pos_tenant_logins_${token}`) || 'null');
        if (saved && Array.isArray(saved.data) && saved.data.length) return saved;
    } catch { /* ignore */ }
    try {
        const b = JSON.parse(localStorage.getItem('pos_offline_auth') || 'null');
        if (!b || (b.waiter_token && b.waiter_token !== token)) return null;
        const seen = new Set();
        const data = [];
        for (const e of (b.passwords || [])) {
            const role = String(e.user?.display_role || 'admin');
            for (const u of (e.usernames || [])) {
                if (seen.has(u)) continue;
                seen.add(u);
                data.push({ username: u, role, label: `${u} (${ROLE_LABEL[role] || role})` });
            }
        }
        return data.length ? { ok: true, data, pin_only: false } : null;
    } catch { return null; }
};

const Login = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Yalnız URL-dəki ?t= tenant konteksti müəyyən edir. Boş kök linkə (pos.fiscalbusiness.net/)
    // girəndə HƏMİŞƏ standart admin girişi göstərilsin — localStorage-dakı köhnə tokenə düşmə
    // (əks halda əvvəl açılmış tenant, məs. demo, PIN ekranında "ilişib" qalırdı).
    // Qeyd: ofisiant kontekstini qorumaq lazım olan bütün daxili yönləndirmələr (401, çıxış)
    // ?t=-ni onsuz da URL-ə geri əlavə edir; desktop avto-çap isə localStorage-dakı
    // pos_waiter_token-i birbaşa oxuyur (aşağıda ?t= olanda yenə yazılır).
    // OFFLINE istisnası: internet yoxdursa ?t= itirsə belə (yönləndirmə/refresh), ofisiant kontekstini
    // localStorage-dan bərpa et (offline PIN ekranı qalsın, admin panelə atmasın). Online davranış dəyişmir.
    const _urlToken = searchParams.get('t') || '';
    const _offlineRestore = netIsOffline()
        ? ((() => {
            try {
                return localStorage.getItem('pos_waiter_token')
                    || (JSON.parse(localStorage.getItem('pos_offline_auth') || 'null') || {}).waiter_token
                    || '';
            } catch { return ''; }
        })())
        : '';
    const waiterToken = _urlToken || _offlineRestore;
    // Offline-da bərpa olunan tokeni URL-ə yaz (?t=). Yoxsa internet qayıdanda bərpa dayanır →
    // PIN ekranı, istifadəçi siyahısı itir və admin girişi açılır.
    useEffect(() => {
        if (_urlToken || !_offlineRestore) return;
        const p = new URLSearchParams(window.location.search);
        p.set('t', _offlineRestore);
        navigate({ search: `?${p.toString()}` }, { replace: true });
    }, [_urlToken, _offlineRestore, navigate]);
    // ?login=staff → birbaşa Admin/İşçi/Mətbəx (şifrə) tab-ı açılsın (məs. mətbəx çıxışından sonra)
    const wantStaffLogin = searchParams.get('login') === 'staff';
    // Superadmin «fon şəkli» önizləmə linki (/?bgpreview=...): təsdiqlənməmiş şəkil göstərilir,
    // giriş etmiş superadmin panelə yönləndirilmir
    const bgPreview = (searchParams.get('bgpreview') || '').slice(0, 64);

    const { user, adminLogin, waiterLogin, waiterPasswordLogin, verifyOtp, superadminRecovery, loading } = useAuthStore();

    // WhatsApp OTP addımı: { challengeId, masked, waiterToken }
    const [otp, setOtp] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [otpBusy, setOtpBusy] = useState(false);
    const [otpError, setOtpError] = useState('');

    // Superadmin recovery kod girişi (WhatsApp itsə)
    const [recoveryOpen, setRecoveryOpen] = useState(false);
    const [recUser, setRecUser] = useState('');
    const [recPass, setRecPass] = useState('');
    const [recCode, setRecCode] = useState('');
    const [recBusy, setRecBusy] = useState(false);
    const [recError, setRecError] = useState('');

    const submitRecovery = async () => {
        if (!recUser.trim() || !recPass || !recCode.trim()) { setRecError('Bütün sahələri doldurun.'); return; }
        setRecBusy(true); setRecError('');
        try {
            const r = await superadminRecovery(recUser.trim(), recPass, recCode.trim());
            if (r.ok) {
                toast.success('Giriş uğurlu.');
                setRecoveryOpen(false);
                navigate(r.redirect || '/superadmin/dashboard');
                return;
            }
            setRecError(r.msg || 'Yanlışdır.');
        } finally {
            setRecBusy(false);
        }
    };

    const [username, setUsername] = useState(getSavedAdminUsername());
    const [password, setPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [loginMode, setLoginMode] = useState((waiterToken && !wantStaffLogin) ? 'waiter' : 'admin');

    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinBusy, setPinBusy] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);

    // Ofisiant ekranında ŞİFRƏ ilə giriş (admin/kassir/administrator) — PIN-dən ayrı
    const [pwMode, setPwMode] = useState(false);
    const [pwValue, setPwValue] = useState('');
    const [pwBusy, setPwBusy] = useState(false);
    const [pwError, setPwError] = useState('');
    const pwRef = useRef(null);

    // #6 — admin login üçün tenant login adları (combobox). waiter_token-dən gəlir.
    const [tenantLogins, setTenantLogins] = useState([]);
    // Superadmin "yalnız ofisiant PIN" seçibsə → admin/işçi/mətbəx tab-ı gizlənir.
    const [pinOnly, setPinOnly] = useState(false);
    const passwordRef = useRef(null);

    const [screenConfig, setScreenConfig] = useState(getCachedScreenConfig(waiterToken));
    const [bgImageReady, setBgImageReady] = useState(() => {
        const cached = getCachedScreenConfig(waiterToken);
        return !cached.login_image_url; // cached yoxdursa default göstər
    });

    useEffect(() => {
        setLoginMode(waiterToken ? 'waiter' : 'admin');
    }, [waiterToken]);

    // waiter_token-u dərhal localStorage-a yaz (login gözləmədən). Belə ki,
    // səhər heç kim login etməsə də desktop app daimi tokeni oxuyub avto-çapı
    // edə bilsin (login ekranında qalsa belə).
    useEffect(() => {
        if (waiterToken) localStorage.setItem('pos_waiter_token', waiterToken);
    }, [waiterToken]);

    // #6 — tenant login adlarını çək (admin panel combobox-u üçün)
    useEffect(() => {
        if (!waiterToken) { setTenantLogins([]); return; }
        let active = true;
        (async () => {
            try {
                let data = null;
                try {
                    const res = await api.get(`/auth/tenant-logins?token=${encodeURIComponent(waiterToken)}`);
                    data = res.data;
                    // Offline üçün ayrıca yadda saxla (keşdən asılı olmasın)
                    try { localStorage.setItem(`pos_tenant_logins_${waiterToken}`, JSON.stringify(data)); } catch { /* ignore */ }
                } catch (e) {
                    if (e?.response) throw e; // server cavab verdi (xəta) → adi davranış
                    data = offlineTenantLogins(waiterToken);
                    if (!data) throw e;
                }
                if (!active) return;
                const list = data?.data || [];
                setTenantLogins(list);
                // "Yalnız ofisiant PIN" aktivdirsə → admin tab-ını gizlə, PIN moduna keç
                const isPinOnly = !!data?.pin_only;
                setPinOnly(isPinOnly);
                if (isPinOnly) setLoginMode('waiter');
                // Sonuncu seçilən login siyahıda varsa onu seç; yoxdursa birincini
                const saved = getSavedAdminUsername();
                const match = list.find((l) => l.username === saved) || list[0];
                if (match) setUsername(match.username);
            } catch {
                if (active) { setTenantLogins([]); setPinOnly(false); }
            }
        })();
        return () => { active = false; };
    }, [waiterToken]);

    // Offline PIN login üçün ofisiant siyahısını online ikən keşlə (internet gedəndə lokal yoxlama)
    useEffect(() => {
        if (!waiterToken) return;
        api.get(`/auth/offline-waiters?token=${encodeURIComponent(waiterToken)}`).catch(() => {});
    }, [waiterToken]);

    // Combobox sinxronu: username HƏMİŞƏ siyahıdakı etibarlı seçim olsun.
    // Əks halda <select> ilk variantı GÖSTƏRİR, amma state boş/köhnə qalır →
    // parol yazıb girəndə "istifadəçi adı yanlışdır" çıxırdı (onChange tetiklənmir).
    useEffect(() => {
        if (tenantLogins.length > 0 && !tenantLogins.some((l) => l.username === username)) {
            setUsername(tenantLogins[0].username);
        }
    }, [tenantLogins, username]);

    useEffect(() => {
        // OFFLINE: mövcud sessiyanı SİLMƏ (offline PIN login üçün token/user qalsın, döngü olmasın).
        // Online: əvvəlki qayda — təmiz giriş ekranı (köhnə tenant stick olmasın).
        const isOnline = netIsOnline();
        if (isOnline) {
            localStorage.removeItem('pos_token');
            localStorage.removeItem('pos_user');
            useAuthStore.setState({ user: null, token: null, loading: false, error: null });
        }
        setSessionReady(true);
    }, []);

    useEffect(() => {
        if (!sessionReady || !user || bgPreview) return;

        // OFFLINE: yalnız sifarişlər işləyir → hamı /waiter/tables-ə (admin dashboard/anbar offline açılmaz)
        const isOnline = netIsOnline();
        if (!isOnline && user.role !== 'superadmin') { navigate('/waiter/tables'); return; }

        if (user.role === 'superadmin') navigate('/superadmin/dashboard');
        else if (user.role === 'admin') navigate('/admin/dashboard');
        else if (user.role === 'anbardar') navigate('/warehouse/dashboard');
        else navigate('/waiter/tables');
    }, [user, navigate, sessionReady, bgPreview]);

    useEffect(() => {
        let active = true;

        const loadScreenConfig = async () => {
            try {
                const qs = new URLSearchParams();
                if (waiterToken) qs.set('token', waiterToken);
                if (bgPreview) qs.set('bgpreview', bgPreview);
                const query = qs.toString() ? `?${qs.toString()}` : '';
                const res = await api.get(`/auth/login-screen-config${query}`);

                if (active && res.data?.ok) {
                    const nextConfig = {
                        ...defaultScreenConfig,
                        ...(res.data.data || {})
                    };

                    setScreenConfig(nextConfig);

                    if (waiterToken && !bgPreview) {
                        localStorage.setItem(`pos_login_screen_${waiterToken}`, JSON.stringify(nextConfig));
                    }
                }
            } catch {
                if (active) {
                    setScreenConfig(getCachedScreenConfig(waiterToken));
                }
            }
        };

        loadScreenConfig();

        return () => {
            active = false;
        };
    }, [waiterToken, bgPreview]);

    const handleAdminLogin = async (e) => {
        e.preventDefault();

        if (!username.trim() || !password.trim()) {
            toast.error('İstifadəçi adı və şifrə daxil edin.');
            return;
        }

        const result = await adminLogin(username.trim(), password);

        if (result.otp_required) {
            localStorage.setItem('pos_last_admin_username', username.trim());
            setOtp({ challengeId: result.challenge_id, masked: result.to_masked, waiterToken: null, scope: result.scope });
            setOtpCode(''); setOtpError('');
            return;
        }
        if (result.ok) {
            localStorage.setItem('pos_last_admin_username', username.trim());
            if (waiterToken) {
                localStorage.setItem('pos_waiter_token', waiterToken);
            }
            toast.success(`Xoş gəldiniz, ${result.user.name}!`);
            navigate(result.redirect || '/admin/dashboard');
            return;
        }
        // Lisenziya bitib → panel yox, ödəniş ekranı
        if (result.code === 'license_expired' && result.pay_ticket) {
            toast.error(result.msg);
            navigate(`/odenis?t=${encodeURIComponent(result.pay_ticket)}`);
            return;
        }

        toast.error(result.msg);
    };

    // OTP kodunu təsdiqlə
    const submitOtp = async () => {
        if (!otp || otpCode.trim().length < 4) { setOtpError('6 rəqəmli kodu daxil edin.'); return; }
        setOtpBusy(true); setOtpError('');
        try {
            // Superadmin → cihazı 30 gün yadda saxla (Windows daxil təkrar OTP istəməsin)
            const r = await verifyOtp(otp.challengeId, otpCode.trim(), otp.waiterToken, otp.scope === 'superadmin');
            if (r.ok) {
                // Ofisiant ekranından (URL-də ?t=) admin OTP ilə girildisə, waiter tokeni
                // BƏRPA et — verifyOtp/storeSession onu silir. Çıxışda PIN ekranı itməsin.
                if (waiterToken) { try { localStorage.setItem('pos_waiter_token', waiterToken); } catch { /* ignore */ } }
                toast.success(`Xoş gəldiniz, ${r.user.name}!`);
                setOtp(null);
                navigate(r.redirect || '/admin/dashboard');
                return;
            }
            setOtpError(r.msg || 'Kod yanlışdır.');
            setOtpCode('');
            if (r.blocked) { toast.error(r.msg); setTimeout(() => setOtp(null), 2500); }
        } finally {
            setOtpBusy(false);
        }
    };

    const submitPin = useCallback(
        async (pinValue) => {
            if (!waiterToken) {
                setPinError('Giriş linki yanlışdır.');
                return;
            }

            setPinBusy(true);
            try {
                const result = await waiterLogin(pinValue, waiterToken);
                if (result.otp_required) {
                    setOtp({ challengeId: result.challenge_id, masked: result.to_masked, waiterToken });
                    setOtpCode(''); setOtpError(''); setPin('');
                    return;
                }
                if (result.ok) {
                    toast.success(`Xoş gəldiniz, ${result.user.name}!`);
                    navigate(result.redirect || '/waiter/tables');
                    return;
                }

                setPinError(result.msg || 'Yanlış PIN.');
                setPin('');
            } finally {
                setPinBusy(false);
            }
        },
        [navigate, waiterLogin, waiterToken]
    );

    const addPin = useCallback(
        (num) => {
            if (pinBusy || pin.length >= 4) return;

            const nextPin = pin + num;
            setPin(nextPin);
            setPinError('');

            if (nextPin.length === 4) {
                submitPin(nextPin);
            }
        },
        [pin, pinBusy, submitPin]
    );

    const removePin = useCallback(() => {
        if (pinBusy) return;
        setPin((prev) => prev.slice(0, -1));
        setPinError('');
    }, [pinBusy]);

    const clearPin = useCallback(() => {
        if (pinBusy) return;
        setPin('');
        setPinError('');
    }, [pinBusy]);

    const submitWaiterPassword = useCallback(async () => {
        const val = pwValue.trim();
        if (!val) { setPwError('Şifrə daxil edin.'); return; }
        if (!waiterToken) { setPwError('Giriş linki yanlışdır.'); return; }
        setPwBusy(true);
        try {
            const result = await waiterPasswordLogin(val, waiterToken);
            if (result.ok) {
                toast.success(`Xoş gəldiniz, ${result.user.name}!`);
                navigate(result.redirect || '/waiter/tables');
                return;
            }
            setPwError(result.msg || 'Şifrə yanlışdır.');
            setPwValue('');
        } finally {
            setPwBusy(false);
        }
    }, [pwValue, waiterToken, waiterPasswordLogin, navigate]);

    // Şifrə moduna keçəndə inputa fokus
    useEffect(() => {
        if (pwMode && pwRef.current) pwRef.current.focus();
    }, [pwMode]);

    useEffect(() => {
        // pwMode aktivdirsə rəqəm düymələri PIN-ə getməsin (istifadəçi şifrə yazır)
        if (!waiterToken || loginMode !== 'waiter' || pwMode) return;

        const handleKey = (e) => {
            if (e.key >= '0' && e.key <= '9') addPin(e.key);
            else if (e.key === 'Backspace') removePin();
            else if (e.key === 'Escape') clearPin();
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [addPin, clearPin, removePin, waiterToken, loginMode]);

    const handleModeChange = (nextMode) => {
        setLoginMode(nextMode);
        setPin('');
        setPinError('');
        setUsername(getSavedAdminUsername());
        setPassword('');
    };

    // Şəkil dəyişdikdə preload et, hazır olanda göstər
    useEffect(() => {
        const url = screenConfig.login_image_url;
        if (!url) { setBgImageReady(true); return; }
        setBgImageReady(false);
        const img = new Image();
        img.onload  = () => setBgImageReady(true);
        img.onerror = () => setBgImageReady(true);
        img.src = url;
    }, [screenConfig.login_image_url]);

    const leftPanelStyle = (screenConfig.login_image_url && bgImageReady)
        ? {
              backgroundImage: `linear-gradient(rgba(5, 17, 27, 0.16), rgba(5, 17, 27, 0.34)), url(${screenConfig.login_image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
          }
        : {
              backgroundImage: 'linear-gradient(rgba(5, 17, 27, 0.16), rgba(5, 17, 27, 0.34)), url(/default_background.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#0d2233'
          };

    return (
        <div className="flex h-[100dvh] w-screen flex-col overflow-hidden lg:h-screen lg:flex-row" style={{ background: '#111009' }}>
            {/* WhatsApp OTP təsdiq ekranı */}
            {otp && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="text-center text-3xl">🔐</div>
                        <h2 className="mt-2 text-center text-lg font-bold text-gray-800">Təsdiq kodu</h2>
                        <p className="mt-1 text-center text-xs text-gray-500">
                            WhatsApp-a göndərilən 6 rəqəmli kodu daxil edin{otp.masked ? ` (${otp.masked})` : ''}.
                        </p>
                        <input
                            autoFocus
                            type="tel"
                            inputMode="numeric"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitOtp(); }}
                            placeholder="______"
                            className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-2xl font-bold tracking-[0.5em] text-gray-800 focus:border-primary-400 focus:outline-none"
                        />
                        {otpError && <p className="mt-2 text-center text-xs font-medium text-rose-600">{otpError}</p>}
                        <button
                            onClick={submitOtp}
                            disabled={otpBusy || otpCode.length < 4}
                            className="mt-4 w-full rounded-xl px-4 py-3 text-sm font-bold text-white transition-all disabled:opacity-50"
                            style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                            {otpBusy ? 'Yoxlanılır…' : 'Təsdiqlə və gir'}
                        </button>
                        <button onClick={() => { setOtp(null); setOtpCode(''); setOtpError(''); }}
                            className="mt-2 w-full rounded-xl px-4 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-50">
                            ← Geri
                        </button>
                    </div>
                </div>
            )}

            {/* Superadmin recovery kod girişi (WhatsApp itsə) */}
            {recoveryOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="text-center text-3xl">🆘</div>
                        <h2 className="mt-2 text-center text-lg font-bold text-gray-800">Superadmin recovery</h2>
                        <p className="mt-1 text-center text-xs text-gray-500">WhatsApp itibsə — username, parol və recovery kodla gir.</p>
                        <input value={recUser} onChange={(e) => { setRecUser(e.target.value); setRecError(''); }}
                            placeholder="Superadmin username"
                            className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-400 focus:outline-none" />
                        <input type="password" value={recPass} onChange={(e) => { setRecPass(e.target.value); setRecError(''); }}
                            placeholder="Parol"
                            className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-400 focus:outline-none" />
                        <input value={recCode} onChange={(e) => { setRecCode(e.target.value.toUpperCase()); setRecError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitRecovery(); }}
                            placeholder="FISCAL-XXXX-XXXX-XXXX"
                            className="mt-2 w-full rounded-xl border border-gray-300 px-4 py-2.5 text-center font-mono text-sm tracking-wider focus:border-primary-400 focus:outline-none" />
                        {recError && <p className="mt-2 text-center text-xs font-medium text-rose-600">{recError}</p>}
                        <button onClick={submitRecovery} disabled={recBusy}
                            className="mt-4 w-full rounded-xl bg-amber-600 px-4 py-3 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50">
                            {recBusy ? 'Yoxlanılır…' : 'Recovery ilə gir'}
                        </button>
                        <button onClick={() => { setRecoveryOpen(false); setRecCode(''); setRecPass(''); setRecError(''); }}
                            className="mt-2 w-full rounded-xl px-4 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-50">
                            ← Geri
                        </button>
                    </div>
                </div>
            )}
            <div
                className="relative flex h-[23vh] flex-none flex-col items-center justify-center overflow-hidden px-6 py-4 sm:py-6 sm:px-8 lg:h-auto lg:flex-1 lg:min-h-0 lg:p-12"
                style={leftPanelStyle}
            >
                <div className="absolute inset-0 bg-black/0" />
                {bgPreview && (
                    <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full bg-amber-400/95 px-4 py-1.5 text-xs font-bold text-amber-950 shadow-lg">
                        {screenConfig.bg_preview ? '👁 Önizləmə: yeni fon şəkli (hələ təsdiqlənməyib)' : 'Önizləmə linki köhnəlib və ya ləğv edilib'}
                    </div>
                )}

                <div className="relative z-10 max-w-xl text-center">
                    <h1 className="mb-3 text-3xl font-black text-white sm:text-4xl md:text-5xl" style={{ fontFamily: 'serif' }}>
                        {screenConfig.app_name || 'Fiscal POS'}
                    </h1>
                    <p className="mb-0 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/85 sm:mb-8 sm:text-sm sm:tracking-[0.35em]">
                        Restoran idarəetmə sistemi
                    </p>
                    <div className="mx-auto mb-6 hidden h-px w-16 bg-white/60 sm:mb-8 sm:block" />
                    <div className="hidden sm:block">
                        <LoginClock />
                    </div>
                </div>

                <div className="absolute bottom-4 left-0 right-0 z-10 hidden px-4 text-center sm:bottom-6 sm:block sm:px-6">
                    <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-white/75 sm:text-xs sm:tracking-[0.28em]">
                        {`${screenConfig.app_name || 'Fiscal POS'} v${screenConfig.app_version || '1.0.0'}`}
                    </p>
                    <p className="text-xs font-bold text-white/95 underline underline-offset-4 sm:text-sm">
                        Powered by{' '}
                        <a
                            href={screenConfig.powered_by_url || 'https://www.fiscalbusiness.net'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-white"
                        >
                            www.fiscalbusiness.net
                        </a>
                    </p>
                </div>
            </div>

            <div className="relative flex w-full flex-1 flex-col items-center justify-center overflow-y-auto bg-white px-5 py-4 sm:px-8 sm:py-4 lg:flex-none lg:max-w-md lg:px-10 lg:py-5">
                {/* FiscalPOS logo — logodan düyməyə qədər bütün blok bir yerdə mərkəzlənir (yuxarı=aşağı boşluq) */}
                <div className="flex w-full items-center justify-center pb-3">
                    <img
                        src="/FiscalPOS_blue.png"
                        alt="FiscalPOS"
                        className="max-h-[15vh] w-auto max-w-[70%] object-contain sm:max-h-[16vh]"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
                {waiterToken && !pinOnly && (
                    <div className="mb-3 flex w-full max-w-xs rounded-2xl border border-gray-200 bg-gray-50 p-1">
                        <button
                            type="button"
                            onClick={() => handleModeChange('waiter')}
                            className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold transition-all ${
                                loginMode === 'waiter'
                                    ? 'bg-primary-600 text-white shadow-sm'
                                    : 'text-gray-500 hover:text-primary-600'
                            }`}
                        >
                            Ofisiant (PIN)
                        </button>
                        <button
                            type="button"
                            onClick={() => handleModeChange('admin')}
                            className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold transition-all ${
                                loginMode === 'admin'
                                    ? 'bg-primary-600 text-white shadow-sm'
                                    : 'text-gray-500 hover:text-primary-600'
                            }`}
                        >
                            Admin / İşçi / Mətbəx
                        </button>
                    </div>
                )}

                {loginMode === 'waiter' ? (
                    <div className="w-full max-w-xs 2xl:max-w-sm">
                        <div className="mb-2 text-center">
                            <div className="mb-1.5 inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1 text-sm font-bold text-primary-600">
                                Ofisiant girişi
                            </div>
                            <h2 className="mb-0.5 text-lg font-bold text-gray-800 sm:text-xl" style={{ fontFamily: 'serif' }}>
                                PIN daxil edin
                            </h2>
                            <p className="text-xs text-gray-500 sm:text-sm">4 rəqəmli PIN kodu yazın</p>
                        </div>

                        <div className="mb-2 flex justify-center gap-4">
                            {[0, 1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className={`h-4 w-4 rounded-full border-2 transition-all duration-200 ${
                                        i < pin.length
                                            ? 'scale-110 border-primary-500 bg-primary-500'
                                            : 'border-primary-200 bg-transparent'
                                    }`}
                                />
                            ))}
                        </div>

                        <div className="mb-2 h-5 text-center">
                            {pinError && <p className="text-sm font-semibold text-red-500">{pinError}</p>}
                            {pinBusy && <p className="text-sm text-primary-500">Yoxlanılır...</p>}
                        </div>

                        <div className="mb-2 grid grid-cols-3 gap-2 sm:gap-2.5 2xl:gap-3">
                            {[
                                { num: '1', sub: '' },
                                { num: '2', sub: 'ABC' },
                                { num: '3', sub: 'DEF' },
                                { num: '4', sub: 'GHI' },
                                { num: '5', sub: 'JKL' },
                                { num: '6', sub: 'MNO' },
                                { num: '7', sub: 'PQRS' },
                                { num: '8', sub: 'TUV' },
                                { num: '9', sub: 'WXYZ' }
                            ].map(({ num, sub }) => (
                                <button
                                    key={num}
                                    onClick={() => addPin(num)}
                                    disabled={pinBusy || pin.length >= 4}
                                    className="flex h-11 flex-col items-center justify-center gap-0.5 rounded-xl border border-gray-200 text-lg font-bold text-gray-700 transition-all hover:border-primary-400 hover:bg-primary-50 hover:text-primary-500 active:scale-95 disabled:opacity-50 sm:text-xl xl:h-[52px] 2xl:h-[64px] 2xl:text-2xl"
                                >
                                    {num}
                                    {sub && <span className="text-[9px] font-semibold tracking-wider text-gray-400">{sub}</span>}
                                </button>
                            ))}

                            <button
                                onClick={clearPin}
                                disabled={pinBusy}
                                className="h-11 xl:h-[52px] 2xl:h-[64px] rounded-xl border border-red-100 text-xs font-bold text-red-400 transition-all hover:bg-red-500 hover:text-white active:scale-95 disabled:opacity-50"
                            >
                                SİL
                            </button>
                            <button
                                onClick={() => addPin('0')}
                                disabled={pinBusy || pin.length >= 4}
                                className="h-11 xl:h-[52px] 2xl:h-[64px] rounded-xl border border-gray-200 text-xl font-bold text-gray-700 transition-all hover:border-primary-400 hover:bg-primary-50 hover:text-primary-500 active:scale-95 disabled:opacity-50 2xl:text-2xl"
                            >
                                0
                            </button>
                            <button
                                onClick={removePin}
                                disabled={pinBusy}
                                className="h-11 xl:h-[52px] 2xl:h-[64px] rounded-xl border border-gray-200 text-xl text-gray-500 transition-all hover:border-primary-400 hover:bg-primary-50 hover:text-primary-500 active:scale-95 disabled:opacity-50"
                            >
                                X
                            </button>
                        </div>

                        {/* Şifrə ilə giriş — admin/kassir/administrator waiter sisteminə düşür */}
                        <div className="mt-0 border-t border-gray-100 pt-2">
                            {!pwMode ? (
                                <button
                                    onClick={() => { setPwMode(true); setPwError(''); setPwValue(''); }}
                                    className="w-full rounded-xl border border-primary-200 bg-primary-50/50 py-2.5 text-sm font-bold text-primary-600 transition-all hover:bg-primary-50"
                                >
                                    🔑 Şifrə ilə giriş
                                </button>
                            ) : (
                                <div>
                                    <label className="mb-1 block text-xs font-semibold text-gray-500">Şifrə (admin / kassir)</label>
                                    <input
                                        ref={pwRef}
                                        type="password"
                                        value={pwValue}
                                        onChange={(e) => { setPwValue(e.target.value); setPwError(''); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitWaiterPassword(); } }}
                                        placeholder="Şifrənizi yazın"
                                        disabled={pwBusy}
                                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm focus:border-primary-400 focus:outline-none transition-all disabled:opacity-50"
                                    />
                                    <div className="min-h-[20px] py-1 text-center">
                                        {pwError && <p className="text-xs font-semibold text-red-500">{pwError}</p>}
                                        {pwBusy && <p className="text-xs text-primary-500">Yoxlanılır...</p>}
                                    </div>
                                    <button
                                        onClick={submitWaiterPassword}
                                        disabled={pwBusy || !pwValue.trim()}
                                        className="w-full rounded-xl bg-primary-600 py-2.5 text-sm font-bold text-white transition-all hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        {pwBusy ? 'Daxil olunur...' : 'Daxil ol'}
                                    </button>
                                    <button
                                        onClick={() => { setPwMode(false); setPwValue(''); setPwError(''); }}
                                        className="mt-2 w-full py-1.5 text-xs font-semibold text-gray-400 hover:text-gray-600"
                                    >
                                        ← PIN girişinə qayıt
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="w-full max-w-xs">
                        <div className="mb-8 text-center">
                            <h2 className="mb-1 text-2xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>
                                Admin panel girişi
                            </h2>
                            {screenConfig.tenant_name && (
                                <p className="text-sm text-gray-500">{screenConfig.tenant_name}</p>
                            )}
                        </div>

                        <form onSubmit={handleAdminLogin} className="flex flex-col gap-4">
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    İstifadəçi adı
                                </label>
                                {tenantLogins.length > 0 ? (
                                    <select
                                        value={username}
                                        onChange={(e) => {
                                            setUsername(e.target.value);
                                            // Login seçiləndə parola fokus et
                                            setTimeout(() => passwordRef.current?.focus(), 0);
                                        }}
                                        className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-sm font-medium transition-all focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                                    >
                                        {tenantLogins.map((l) => (
                                            <option key={l.username} value={l.username}>
                                                {l.username}{l.role ? `  (${l.role})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="Məs: demo və ya anbar_demo"
                                        autoFocus
                                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm font-medium transition-all focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                                    />
                                )}
                            </div>

                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    Şifrə
                                </label>
                                <div className="relative">
                                    <input
                                        ref={passwordRef}
                                        type={showPass ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••"
                                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 pr-12 text-sm font-medium transition-all focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPass(!showPass)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPass ? 'Gizlət' : 'Göstər'}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="mt-2 w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                                style={{
                                    background: 'linear-gradient(135deg, #006f91, #0090bb)',
                                    boxShadow: '0 4px 20px rgba(0,144,187,0.25)'
                                }}
                            >
                                {loading ? 'Yoxlanılır...' : 'Daxil ol'}
                            </button>
                            <button type="button" onClick={() => { setRecoveryOpen(true); setRecError(''); }}
                                className="mt-1 text-center text-[11px] font-semibold text-gray-400 hover:text-gray-600">
                                🆘 Superadmin — recovery kod ilə gir
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;
